import { useEffect, useState, useRef, useCallback } from "react";
import { MapPin, Users, FirstAid, Stethoscope, UserCircle, ArrowLeft, CircleNotch, Money } from "@phosphor-icons/react";
import { motion } from "framer-motion";
import api from "../lib/api";
import { fmt, fmtCompact } from "../lib/format";
import PatientMap from "./PatientMap";

function shortDoctor(name) {
  return String(name).replace(/^dr\.?\s*/i, "").replace(/^drg\.?\s*/i, "");
}

function MiniBarList({ items, color, tint, labelFn, testid }) {
  const max = items.length ? items[0].count : 1;
  if (!items.length) return <p className="text-xs text-muted-foreground">Tidak ada data</p>;
  return (
    <div className="space-y-2.5">
      {items.map((d, i) => (
        <motion.div key={i} initial={{ opacity: 0, x: 6 }} animate={{ opacity: 1, x: 0 }}
          transition={{ delay: i * 0.03 }} data-testid={`${testid}-${i}`}>
          <div className="flex items-center justify-between gap-2 text-xs mb-1">
            <span className="truncate flex items-center gap-2 min-w-0">
              <span className="w-4 text-[10px] font-bold tabular shrink-0" style={{ color }}>{i + 1}</span>
              <span className="truncate" title={d.name}>{labelFn ? labelFn(d.name) : d.name}</span>
            </span>
            <span className="tabular font-bold shrink-0">{fmt(d.count)}</span>
          </div>
          <div className="h-1.5 rounded-full overflow-hidden" style={{ background: tint }}>
            <div className="h-full rounded-full" style={{ width: `${(d.count / max) * 100}%`, background: color }} />
          </div>
        </motion.div>
      ))}
    </div>
  );
}

function StatChip({ icon: Icon, label, value, display }) {
  return (
    <div className="rounded-xl bg-secondary/60 px-3 py-2 flex items-center gap-2.5">
      <span className="w-8 h-8 rounded-lg bg-card flex items-center justify-center shrink-0">
        <Icon size={16} className="text-primary" weight="fill" />
      </span>
      <div>
        <div className="font-display font-extrabold text-base tabular leading-none">{display != null ? display : fmt(value)}</div>
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mt-0.5">{label}</div>
      </div>
    </div>
  );
}

export default function KunjunganSection({ dsId, month, onMonths }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const tries = useRef(0);
  const timer = useRef(null);
  const monthParam = month && month !== "all" ? month : undefined;

  useEffect(() => {
    let cancelled = false;
    tries.current = 0;
    setLoading(true);
    setData(null);

    const warm = async () => {
      if (cancelled || tries.current >= 12) return;
      tries.current += 1;
      try {
        const { data: g } = await api.get(`/datasources/${dsId}/kunjungan/geocode`, { params: { month: monthParam } });
        if (cancelled || !g.available) return;
        setData((d) => (d ? { ...d, points: g.points, geocoded: g.geocoded, pending: g.pending } : d));
        if (g.pending > 0) timer.current = setTimeout(warm, 1200);
      } catch { /* keep last state */ }
    };

    (async () => {
      try {
        const { data: res } = await api.get(`/datasources/${dsId}/kunjungan`, { params: { month: monthParam } });
        if (cancelled) return;
        setData(res);
        setLoading(false);
        if (res.available && res.months) onMonths?.(res.months);
        if (res.available && res.pending > 0) warm();
      } catch {
        if (cancelled) return;
        setData({ available: false });
        setLoading(false);
      }
    })();

    return () => { cancelled = true; if (timer.current) clearTimeout(timer.current); };
  }, [dsId, monthParam, onMonths]);

  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const selectKec = useCallback(async (row) => {
    setSelected(row);
    setDetail(null);
    setDetailLoading(true);
    try {
      const { data: res } = await api.get(`/datasources/${dsId}/kunjungan/detail`, {
        params: { kecamatan: row.kecamatan, kota: row.kota || "", provinsi: row.provinsi || "", month: monthParam },
      });
      setDetail(res && res.available ? res : null);
    } catch {
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }, [dsId, monthParam]);

  const clearSelection = () => { setSelected(null); setDetail(null); };

  useEffect(() => { clearSelection(); }, [dsId, monthParam]);

  if (loading && !data) {
    return <div className="text-xs text-muted-foreground animate-pulse mt-6" data-testid="kunjungan-loading">Memuat populasi pasien &amp; diagnosa…</div>;
  }
  if (!data || !data.available) return null;

  const ranking = data.ranking || [];
  const maxRank = ranking.length ? ranking[0].patients : 1;
  const diagnoses = data.diagnoses || [];
  const maxDiag = diagnoses.length ? diagnoses[0].count : 1;
  const empty = (data.total_visits || 0) === 0;

  return (
    <div className="mt-6 space-y-6" data-testid="kunjungan-section">
      {/* Population + Map */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          <MapPin size={20} weight="fill" className="text-primary" />
          <h2 className="font-display font-bold text-lg tracking-tight">Populasi Pasien per Kecamatan</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-3">Sebaran domisili pasien (berdasarkan ID Tagihan pada dbpenjualan2026)</p>

        <div className="flex flex-wrap gap-3 mb-4">
          <StatChip icon={Users} label="Total Pasien" value={data.total_patients} />
          <StatChip icon={FirstAid} label="Kunjungan" value={data.total_visits} />
          <StatChip icon={MapPin} label="Kecamatan" value={data.total_kecamatan} />
          <StatChip icon={Money} label="Final Amount" display={`Rp ${fmtCompact(data.total_amount || 0)}`} />
        </div>

        {empty && (
          <div className="rounded-xl bg-accent/50 ring-1 ring-primary/10 px-4 py-3 text-sm text-muted-foreground mb-4" data-testid="kunjungan-empty">
            Tidak ada data kunjungan untuk periode ini. Data kunjungan pasien tersedia pada bulan tertentu saja — pilih <span className="font-semibold text-foreground">Semua Bulan</span> atau bulan dengan data untuk melihat sebaran.
          </div>
        )}

        {!empty && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          <div className="lg:col-span-8 rounded-2xl bg-card p-3 shadow-sm ring-1 ring-black/[0.03]">
            <PatientMap points={data.points || []} onSelect={selectKec} />
            {data.pending > 0 && (
              <p className="text-[11px] text-muted-foreground mt-2 px-1">Memuat titik peta… {data.geocoded} dari {data.geocoded + data.pending} kecamatan teratas.</p>
            )}
          </div>
          <div className="lg:col-span-4 rounded-2xl bg-card p-4 shadow-sm ring-1 ring-black/[0.03]">
            {selected ? (
              <div data-testid="kecamatan-detail">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="min-w-0">
                    <h3 className="font-display font-bold text-base truncate" title={selected.kecamatan}>{selected.kecamatan}</h3>
                    <p className="text-[11px] text-muted-foreground truncate">{selected.kota}{selected.provinsi ? `, ${selected.provinsi}` : ""}</p>
                  </div>
                  <button onClick={clearSelection} data-testid="kecamatan-detail-back"
                    className="shrink-0 inline-flex items-center gap-1 text-[11px] font-semibold text-primary hover:underline">
                    <ArrowLeft size={13} weight="bold" /> Semua
                  </button>
                </div>

                {detailLoading ? (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground py-8 justify-center">
                    <CircleNotch size={16} className="animate-spin" /> Memuat detail…
                  </div>
                ) : !detail ? (
                  <p className="text-xs text-muted-foreground py-6">Detail tidak tersedia.</p>
                ) : (
                  <div className="max-h-[360px] overflow-y-auto pr-1">
                    <div className="flex gap-2 mb-4">
                      <div className="flex-1 rounded-lg bg-secondary/60 px-2.5 py-1.5">
                        <div className="font-display font-extrabold text-sm tabular leading-none">{fmt(detail.patients)}</div>
                        <div className="text-[9px] uppercase tracking-wider text-muted-foreground font-semibold mt-0.5">Pasien</div>
                      </div>
                      <div className="flex-1 rounded-lg bg-secondary/60 px-2.5 py-1.5">
                        <div className="font-display font-extrabold text-sm tabular leading-none">{fmt(detail.visits)}</div>
                        <div className="text-[9px] uppercase tracking-wider text-muted-foreground font-semibold mt-0.5">Kunjungan</div>
                      </div>
                      <div className="flex-1 rounded-lg bg-secondary/60 px-2.5 py-1.5">
                        <div className="font-display font-extrabold text-sm tabular leading-none" title={fmt(detail.amount)}>Rp {fmtCompact(detail.amount || 0)}</div>
                        <div className="text-[9px] uppercase tracking-wider text-muted-foreground font-semibold mt-0.5">Final Amount</div>
                      </div>
                    </div>

                    <div className="flex items-center gap-1.5 mb-2">
                      <Stethoscope size={15} weight="fill" className="text-primary" />
                      <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Diagnosa Teratas</p>
                    </div>
                    <MiniBarList items={detail.diagnoses || []} color="#5FA777" tint="#EAF5EC" testid="detail-diagnosa" />

                    <div className="flex items-center gap-1.5 mb-2 mt-4">
                      <UserCircle size={15} weight="fill" className="text-primary" />
                      <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Dokter Teratas</p>
                    </div>
                    <MiniBarList items={detail.doctors || []} color="#1C4A37" tint="#E7F2EA" labelFn={shortDoctor} testid="detail-dokter" />
                  </div>
                )}
              </div>
            ) : (
              <>
                <h3 className="font-display font-bold text-base mb-1">Kecamatan Terpadat</h3>
                <p className="text-[11px] text-muted-foreground mb-3">Klik titik peta atau baris untuk detail</p>
                <div className="space-y-3 max-h-[360px] overflow-y-auto pr-1">
                  {ranking.slice(0, 12).map((r, i) => (
                    <motion.button type="button" key={`${r.kecamatan}-${i}`} initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.03 }} onClick={() => selectKec(r)}
                      className="w-full text-left group" data-testid={`kecamatan-row-${i}`}>
                      <div className="flex items-center justify-between gap-2 text-xs mb-1">
                        <span className="truncate flex items-center gap-2">
                          <span className="w-5 h-5 rounded-md bg-secondary text-[10px] font-bold flex items-center justify-center shrink-0 tabular">{i + 1}</span>
                          <span className="truncate group-hover:text-primary transition-colors" title={`${r.kecamatan}, ${r.kota}`}>{r.kecamatan}</span>
                        </span>
                        <span className="text-right shrink-0 leading-tight">
                          <span className="block tabular font-bold">{fmt(r.patients)}</span>
                          <span className="block text-[9px] text-muted-foreground tabular">Rp {fmtCompact(r.amount || 0)}</span>
                        </span>
                      </div>
                      <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
                        <div className="h-full rounded-full bg-primary" style={{ width: `${(r.patients / maxRank) * 100}%` }} />
                      </div>
                    </motion.button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
        )}
      </div>

      {/* Top diagnoses */}
      {!empty && (
      <div className="rounded-2xl bg-card p-4 shadow-sm ring-1 ring-black/[0.03]" data-testid="diagnosa-panel">
        <div className="flex items-center gap-2 mb-1">
          <Stethoscope size={20} weight="fill" className="text-primary" />
          <h2 className="font-display font-bold text-lg tracking-tight">Diagnosa Terbanyak</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-4">Diagnosa paling sering dari dbkunjungan (berdasarkan ID Tagihan dbpenjualan2026)</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
          {diagnoses.map((d, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.02 }} data-testid={`diagnosa-row-${i}`}>
              <div className="flex items-center justify-between gap-2 text-xs mb-1">
                <span className="truncate flex items-center gap-2">
                  <span className="w-5 h-5 rounded-md bg-accent text-[10px] font-bold flex items-center justify-center shrink-0 tabular text-primary">{i + 1}</span>
                  <span className="truncate" title={d.name}>{d.name}</span>
                </span>
                <span className="tabular font-bold shrink-0">{fmt(d.count)}</span>
              </div>
              <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${(d.count / maxDiag) * 100}%`, background: "#5FA777" }} />
              </div>
            </motion.div>
          ))}
        </div>
      </div>
      )}
    </div>
  );
}
